import type {
  AuthSessionResponse,
  BrowserEvent,
  CreateFileSecretMetaInput,
  CreateSecretResponse,
  CreateTextSecretInput,
  OtpRequestInput,
  OtpRequestResponse,
  OtpVerifyInput,
  OtpVerifyResponse,
  SecretMetaResponse,
} from "@sss/shared";
import { API_BASE_URL, BROWSER_EVENTS_ENABLED } from "./config";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: { error?: string; [key: string]: unknown } | undefined,
  ) {
    super(`API error ${status}: ${body?.error ?? "unknown"}`);
  }
}

async function parseJsonOrThrow<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new ApiError(response.status, body);
  }
  return body as T;
}

/**
 * The header carrying a reply grant, for a recipient answering a secret on
 * a deployment where SSO gates creation. Absent on every ordinary create,
 * where the session cookie (or nothing at all) decides instead.
 */
const REPLY_GRANT_HEADER = "X-Reply-Grant";

function replyGrantHeader(token?: string): Record<string, string> {
  return token ? { [REPLY_GRANT_HEADER]: token } : {};
}

export async function createTextSecret(
  input: CreateTextSecretInput,
  replyGrantToken?: string,
): Promise<CreateSecretResponse> {
  const response = await fetch(`${API_BASE_URL}/api/secrets/text`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...replyGrantHeader(replyGrantToken) },
    credentials: "include",
    body: JSON.stringify(input),
  });
  return parseJsonOrThrow(response);
}

/**
 * Uploads an already-encrypted file. `encryptedBody` is either the
 * ReadableStream produced by OpenPGP.js's streaming encrypt or a Blob.
 *
 * We deliberately do NOT use fetch's experimental streaming-upload mode
 * (`duplex: "half"` with a ReadableStream body) here, even though Chromium
 * reports support for it via `"duplex" in Request.prototype`. In practice
 * it has caused hard network-stack failures (e.g. ERR_ALPN_NEGOTIATION_FAILED)
 * that are unrelated to anything the app controls. The encrypted bytes are
 * bounded by MAX_FILE_SIZE_MB either way, so buffering into a Blob before
 * upload is a small, predictable cost worth paying for reliability.
 */
export async function createFileSecret(
  meta: CreateFileSecretMetaInput,
  encryptedBody: ReadableStream<Uint8Array> | Blob,
  replyGrantToken?: string,
): Promise<CreateSecretResponse> {
  const metaHeader = btoa(unescape(encodeURIComponent(JSON.stringify(meta))));
  const body = encryptedBody instanceof Blob ? encryptedBody : await new Response(encryptedBody).blob();

  const response = await fetch(`${API_BASE_URL}/api/secrets/file`, {
    method: "POST",
    headers: {
      "Content-Type": "application/octet-stream",
      "X-Secret-Meta": metaHeader,
      ...replyGrantHeader(replyGrantToken),
    },
    credentials: "include",
    body,
  });

  return parseJsonOrThrow(response);
}

export async function getSecretMeta(id: string): Promise<SecretMetaResponse> {
  const response = await fetch(`${API_BASE_URL}/api/secrets/${id}/meta`);
  return parseJsonOrThrow(response);
}

export async function requestOtp(id: string, input: OtpRequestInput): Promise<OtpRequestResponse> {
  const response = await fetch(`${API_BASE_URL}/api/secrets/${id}/otp/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return parseJsonOrThrow(response);
}

export async function verifyOtp(id: string, input: OtpVerifyInput): Promise<OtpVerifyResponse> {
  const response = await fetch(`${API_BASE_URL}/api/secrets/${id}/otp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return parseJsonOrThrow(response);
}

/** Returns the raw fetch Response so callers can read either .json() (text) or .body (file stream). */
export async function downloadSecret(id: string, retrievalToken: string): Promise<Response> {
  const response = await fetch(
    `${API_BASE_URL}/api/secrets/${id}/download?token=${encodeURIComponent(retrievalToken)}`,
  );
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, body);
  }
  return response;
}

export async function getAuthSession(): Promise<AuthSessionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/session`, { credentials: "include" });
  return parseJsonOrThrow(response);
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
}

/**
 * Reports one retrieval step — the part of a retrieval the server can't
 * observe, because decryption happens here. Unlike every other function in
 * this file it never throws and never returns anything: telemetry must not
 * be able to break a retrieval.
 *
 * `BrowserEvent` is an allowlist of enums and numbers (see
 * packages/shared/src/schemas.ts), so there is no way to pass a URL, the
 * passphrase, a filename, or an error message through it — and by this
 * point `location.href` no longer holds the key either (lib/linkKey.ts).
 */
export function reportBrowserEvent(event: BrowserEvent): void {
  if (!BROWSER_EVENTS_ENABLED) return;
  void fetch(`${API_BASE_URL}/api/telemetry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // No credentials: this says nothing about who is retrieving, and a
    // session cookie would make it look like it did.
    body: JSON.stringify(event),
    keepalive: true,
  }).catch(() => {
    // A dropped metric is not worth a console error on a page whose job is
    // to show someone their secret.
  });
}
